import { InfoPopover, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { TrendLabel } from '@/api/rest/resources/conservation';
import { ConservationBenchmark, PlotReportFactElement, R1FactType } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { TrendPill } from '@/components/Pill/ConservationPill';
import { getDisplayNumber, useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useTrendLabelFromPlotFact } from '@/hooks/useTrendLabelFromFact';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';

export const HabitatIntactnessStatsTile = ({
  latestFact,
  baselineFact,
  trendFact,
  benchmark,
}: {
  latestFact: PlotReportFactElement<number | null> | undefined;
  baselineFact: PlotReportFactElement<number | null> | undefined;
  trendFact: PlotReportFactElement<number | null> | undefined;
  benchmark: ConservationBenchmark | null;
}) => {
  const { t } = useTranslation();

  const trendLabel = useTrendLabelFromPlotFact({
    fact: R1FactType.habitat_intactness_total_previous_5y_pct_change,
  });

  const { benchmarkText, trendText } = useGetExplainerText({
    benchmark: latestFact?.value
      ? {
          label: benchmark,
          value: latestFact.value,
        }
      : undefined,
    trend: trendFact?.value
      ? {
          label: trendLabel,
          value: trendFact?.value,
        }
      : undefined,
  });

  const latestAnalysisDate = printLatestDate([latestFact?.measured_at]);
  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const baselineMeasuredAtDate = printLatestDate([baselineFact?.measured_at]);

  let habitatIntactnessTrendDisplay = `${useDisplayNumber(Number(trendFact?.value ?? 0), {
    signDisplay: 'always',
  })} %`;
  habitatIntactnessTrendDisplay = !isNullish(trendFact?.value) ? habitatIntactnessTrendDisplay : '-';

  const explainerText =
    benchmarkText && trendText ? `${benchmarkText}\n\n${trendText}` : benchmarkText || trendText || null;

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='habitat-intactness-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.habitatIntactness.stats.header.title')}
          supplementaryInfo={
            <Stack direction='row' spacing={2} className='w-full justify-end'>
              <span>{`${latestAnalysisTranslation} ${latestAnalysisDate}`}</span>
              <InfoPopover
                position='top'
                body={t('shared.ncaDetail.details.habitatIntactness.labels.missingDataContext')}
              />
            </Stack>
          }
          popover={{
            title: t('shared.ncaDetail.details.habitatIntactness.stats.header.title'),
            body: t('shared.ncaDetail.details.habitatIntactness.stats.header.tooltip'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-16'>
          {latestFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.latestAnalysis')}
              fact={latestFact}
              capsuleIconProps={{
                icon: getNCChangeIcon(latestFact),
                variant: getNCChangeIconVariant(latestFact),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
                body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
                  date: latestAnalysisDate,
                }),
                popoverTriggerProps: {
                  'data-testid': 'habitat-intactness-latest-analysis-popover',
                },
              }}
              trend={
                typeof trendFact?.value === 'number' && (
                  <CapsuleTrend variant={trendFact.value < 0 ? 'negative' : 'positive'}>
                    {habitatIntactnessTrendDisplay}
                  </CapsuleTrend>
                )
              }
            />
          )}
          {baselineFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.habitatIntactness.explainers.baseline.title')}
              capsuleIconProps={{
                icon: getNCChangeIcon(baselineFact),
                variant: getNCChangeIconVariant(baselineFact),
              }}
              fact={baselineFact}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.habitatIntactness.explainers.baseline.title'),
                body: t('shared.ncaDetail.details.habitatIntactness.explainers.baseline.body', {
                  date: baselineMeasuredAtDate,
                }),
              }}
            />
          )}
        </Stack>
        {explainerText && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <Stack direction='row' spacing={4}>
              {benchmark && <BenchmarkPill benchmarkType={benchmark} />}
              {trendLabel && <TrendPill trendLabel={trendLabel} />}
            </Stack>
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.habitatIntactness.explainers.whatDoesThisMean.title')}
            </span>
            <p className='typography-body1'>{explainerText}</p>
          </Stack>
        )}
      </div>
    </Stack>
  );
};

const useGetExplainerText = ({
  benchmark,
  trend,
}: {
  benchmark?: {
    label: ConservationBenchmark | null;
    value: number;
  };
  trend?: {
    label: TrendLabel | null;
    value: number;
  };
}): {
  benchmarkText: string | null;
  trendText: string | null;
} => {
  const { t } = useTranslation();
  const unit = formatUnit(UnitEnum['%']);

  const benchmarkText =
    benchmark?.label && !isNullish(benchmark?.value)
      ? t(
          `shared.ncaDetail.details.habitatIntactness.explainers.whatDoesThisMean.body.benchmark.${benchmark.label}`,
          '',
          {
            value: `${getDisplayNumber(benchmark.value, undefined, {
              signDisplay: 'never',
            })} ${unit}`,
          },
        )
      : null;

  const trendText =
    trend?.label && !isNullish(trend?.value)
      ? t(`shared.ncaDetail.details.habitatIntactness.explainers.whatDoesThisMean.body.trendLabel.${trend.label}`, '', {
          value: `${getDisplayNumber(trend.value, undefined, {
            signDisplay: 'always',
          })} ${unit}`,
        })
      : null;

  return { benchmarkText: benchmarkText ?? null, trendText: trendText ?? null };
};
